import { jsPDF } from 'jspdf';
import { buildConstanciaBodyRuns, CONSTANCIA_DISCLAIMER, formatDateShort, tokenizeRuns, type ConstanciaData, type Run } from './constanciaText';

export type { ConstanciaData };
export { CONSTANCIA_DISCLAIMER, buildConstanciaBodyRuns };

/**
 * Template de la "Constancia de Atención" — réplica visual y textual exacta
 * del documento que genera psima (`constancia-atencion-pdf-document.tsx`,
 * @react-pdf/renderer). Acá se re-implementa con jsPDF (la librería de PDF
 * que ya usa psi-platform) porque no vale la pena sumar una segunda
 * dependencia de PDF solo por esta feature — pero el layout, los colores, la
 * redacción y el nombre de archivo están portados 1:1 desde el original.
 *
 * Unidad de trabajo: puntos (pt), igual que react-pdf, para poder copiar los
 * valores de su StyleSheet (paddingTop/paddingHorizontal/fontSize/etc.) sin
 * tener que convertir a mm.
 */

const COLORS = {
  navy: [45, 32, 73] as [number, number, number], // #2D2049
  purpleMid: [102, 80, 144] as [number, number, number], // #665090
  grayDark: [51, 51, 51] as [number, number, number], // #333333
  grayMid: [97, 97, 97] as [number, number, number], // #616161
};

export interface RenderConstanciaOptions {
  /** Logo institucional pre-rasterizado (PNG data URL). Sin logo/watermark si se omite. */
  logoDataUrl?: string | null;
  /** Inyectable para tests; por defecto la fecha actual. */
  issuedAt?: Date;
}

/**
 * Dibuja texto justificado (como `textAlign: 'justify'` de react-pdf) con
 * negrita palabra por palabra. Devuelve el `y` final (después de la última línea).
 */
function drawJustifiedRuns(
  doc: jsPDF,
  runs: Run[],
  x: number,
  yTop: number,
  maxWidth: number,
  fontSize: number,
  lineHeight: number,
  color: [number, number, number],
): number {
  const words = tokenizeRuns(runs);
  const setFont = (bold: boolean) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
  };
  const spaceWidth = (() => { setFont(false); return doc.getTextWidth(' '); })();

  // Greedy line-wrap
  const lines: { text: string; bold: boolean; width: number }[][] = [];
  let current: { text: string; bold: boolean; width: number }[] = [];
  let currentWidth = 0;
  for (const word of words) {
    setFont(word.bold);
    const w = doc.getTextWidth(word.text);
    const addWidth = current.length === 0 ? w : w + spaceWidth;
    if (current.length > 0 && currentWidth + addWidth > maxWidth) {
      lines.push(current);
      current = [{ ...word, width: w }];
      currentWidth = w;
    } else {
      current.push({ ...word, width: w });
      currentWidth += addWidth;
    }
  }
  if (current.length) lines.push(current);

  doc.setTextColor(...color);
  let y = yTop;
  lines.forEach((line, idx) => {
    const isLastLine = idx === lines.length - 1;
    const naturalWidth = line.reduce((sum, w) => sum + w.width, 0);
    const gapCount = line.length - 1;
    let gapWidth = spaceWidth;
    if (!isLastLine && gapCount > 0) {
      const extra = maxWidth - (naturalWidth + spaceWidth * gapCount);
      gapWidth = spaceWidth + extra / gapCount;
    }
    let cx = x;
    line.forEach((word) => {
      setFont(word.bold);
      doc.text(word.text, cx, y);
      cx += word.width + gapWidth;
    });
    y += lineHeight;
  });

  return y;
}

/** Dibuja la Constancia en un documento jsPDF nuevo y lo devuelve (sin descargarlo). */
export function renderConstanciaPdf(data: ConstanciaData, opts: RenderConstanciaOptions = {}): jsPDF {
  const issuedAt = opts.issuedAt ?? new Date();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN_X = 56;
  const CONTENT_W = PAGE_W - MARGIN_X * 2;

  // ── Watermark: el mismo logo, grande y muy tenue, detrás del contenido ─────
  if (opts.logoDataUrl) {
    const wmSize = PAGE_W * 0.56;
    const wmX = PAGE_W * 0.22;
    const wmY = PAGE_H * 0.32;
    doc.saveGraphicsState();
    doc.setGState(new (doc as unknown as { GState: new (p: Record<string, number>) => unknown }).GState({ opacity: 0.06 }));
    doc.addImage(opts.logoDataUrl, 'PNG', wmX, wmY, wmSize, wmSize);
    doc.restoreGraphicsState();
  }

  // ── Membrete: marca a la izquierda, logo a la derecha ───────────────────────
  let y = 50;
  const headerTop = y;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.navy);
  doc.text('PSI MAMMOLITI', MARGIN_X, y + 16, { charSpace: 1.5 });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.purpleMid);
  doc.text('www.psimammoliti.com', MARGIN_X, y + 32);

  if (opts.logoDataUrl) {
    doc.addImage(opts.logoDataUrl, 'PNG', PAGE_W - MARGIN_X - 44, headerTop, 44, 44);
  }

  y = headerTop + 44 + 16; // headerRow height (44, el logo manda) + marginTop del divider
  doc.saveGraphicsState();
  doc.setGState(new (doc as unknown as { GState: new (p: Record<string, number>) => unknown }).GState({ opacity: 0.35, 'stroke-opacity': 0.35 }));
  doc.setDrawColor(...COLORS.purpleMid);
  doc.setLineWidth(1);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  doc.restoreGraphicsState();
  y += 1 + 32; // línea + marginBottom del divider

  // ── Título ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.grayDark);
  const titleY = y + 11;
  doc.text('CONSTANCIA', PAGE_W / 2, titleY, { align: 'center' });
  const titleWidth = doc.getTextWidth('CONSTANCIA');
  doc.setLineWidth(0.75);
  doc.setDrawColor(...COLORS.grayDark);
  doc.line(PAGE_W / 2 - titleWidth / 2, titleY + 2, PAGE_W / 2 + titleWidth / 2, titleY + 2);
  y = titleY + 16 + 26 - 11; // baseline -> box bottom + marginBottom

  // ── Fecha de emisión ──────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...COLORS.grayDark);
  const fechaY = y + 8;
  doc.text('Fecha:', MARGIN_X, fechaY);
  const fechaLabelWidth = doc.getTextWidth('Fecha: ');
  doc.setFont('helvetica', 'normal');
  doc.text(formatDateShort(issuedAt), MARGIN_X + fechaLabelWidth, fechaY);
  y = fechaY + 4 + 22; // box bottom + marginBottom

  // ── Cuerpo ────────────────────────────────────────────────────────────────
  const bodyRuns = buildConstanciaBodyRuns(data);
  const bodyY = y + 9;
  const afterBody = drawJustifiedRuns(doc, bodyRuns, MARGIN_X, bodyY, CONTENT_W, 11, 17.6, COLORS.grayDark);
  y = afterBody - 17.6 + 5 + 16; // última línea + marginBottom del párrafo

  // ── Disclaimer ────────────────────────────────────────────────────────────
  const disclaimerRuns: Run[] = [{ text: CONSTANCIA_DISCLAIMER }];
  const disclaimerY = y + 7;
  const afterDisclaimer = drawJustifiedRuns(doc, disclaimerRuns, MARGIN_X, disclaimerY, CONTENT_W, 9.5, 14.25, COLORS.grayMid);
  y = afterDisclaimer - 14.25 + 4 + 16;

  // ── Cierre ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.grayDark);
  doc.text('Sin otro particular, saludamos atentamente.', MARGIN_X, y + 9);

  // ── Footer fijo (membrete al pie, replicado del header) ──────────────────
  const footerBoxHeight = 1 + 10 + 12 + 2 + 10;
  const footerTop = PAGE_H - 40 - footerBoxHeight;
  doc.setDrawColor(...COLORS.purpleMid);
  doc.setLineWidth(1);
  doc.line(MARGIN_X, footerTop, PAGE_W - MARGIN_X, footerTop);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.navy);
  doc.text('Psi Mammoliti', MARGIN_X, footerTop + 10 + 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.purpleMid);
  doc.text('www.psimammoliti.com', MARGIN_X, footerTop + 10 + 9 + 2 + 9);

  return doc;
}
